import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../ui/chart'
import type { ChartDataType } from './app-bar-chart'

export interface AppAreaChartProps<TChartXAxisKey extends string, TChartYAxisKey extends string> {
  xAxisKey: TChartXAxisKey
  yAxisKey: TChartYAxisKey
  data: ChartDataType<TChartXAxisKey, TChartYAxisKey>[]
  xLabelFormatter?: (value: ChartDataType<TChartXAxisKey, TChartYAxisKey>[TChartXAxisKey]) => string
  tooltipLabel?: string
  tooltipLabelFormatter?: (
    value: ChartDataType<TChartXAxisKey, TChartYAxisKey>[TChartXAxisKey],
  ) => string
}

export const AppAreaChart = <TChartXAxisKey extends string, TChartYAxisKey extends string>({
  data,
  xAxisKey,
  yAxisKey,
  xLabelFormatter,
  tooltipLabel,
  tooltipLabelFormatter,
}: AppAreaChartProps<TChartXAxisKey, TChartYAxisKey>) => {
  const chartConfig = {
    [yAxisKey]: {
      label: tooltipLabel,
      color: 'var(--chart-1)',
    },
  } satisfies ChartConfig

  return (
    <ChartContainer config={chartConfig}>
      <AreaChart accessibilityLayer data={data}>
        <CartesianGrid vertical={false} />

        <XAxis
          dataKey={xAxisKey}
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          minTickGap={12}
          tickFormatter={xLabelFormatter ?? ((v) => v.slice(0, 3))}
        />

        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              hideLabel={!tooltipLabelFormatter}
              labelFormatter={tooltipLabelFormatter}
            />
          }
        />

        <defs>
          <linearGradient id="fillChart1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.8} />
            <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0.2} />
          </linearGradient>
        </defs>

        <Area dataKey={yAxisKey} type="monotoneX" fill="url(#fillChart1)" stroke="var(--chart-1)" />
      </AreaChart>
    </ChartContainer>
  )
}
